import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { api } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { 
  TrendingUp, TrendingDown, ArrowDownToLine, ArrowUpFromLine, Store, 
  Search, Filter, Download, FileText, FileSpreadsheet, Calendar
} from 'lucide-react';
import { toast } from 'sonner';

export default function TransactionsPage() {
  const { formatCurrency, preferredCurrency, getSymbol } = useCurrency();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0], // Jan 1 current year
    endDate: new Date().toISOString().split('T')[0] // Today
  });

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const res = await api.get('/payments/transactions');
        setTransactions(res.data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchTransactions();
  }, []);

  const getIcon = (type) => {
    switch (type) {
      case 'deposit': return <ArrowDownToLine className="w-5 h-5" />;
      case 'withdraw': return <ArrowUpFromLine className="w-5 h-5" />;
      case 'buy': return <TrendingUp className="w-5 h-5" />;
      case 'sell': return <TrendingDown className="w-5 h-5" />;
      case 'business_invest': return <Store className="w-5 h-5" />;
      default: return <TrendingUp className="w-5 h-5" />;
    }
  };

  const getColor = (type) => {
    if (['deposit', 'sell'].includes(type)) return 'bg-[#00E676]/20 text-[#00E676]';
    if (['withdraw', 'buy', 'business_invest'].includes(type)) return 'bg-[#FF3B30]/20 text-[#FF3B30]';
    return 'bg-white/20 text-white';
  };

  const filteredTransactions = transactions.filter(t => {
    if (filterType !== 'all' && t.type !== filterType) return false;
    if (searchQuery) {
      return t.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
             t.type.toLowerCase().includes(searchQuery.toLowerCase());
    }
    return true;
  });

  // Filter transactions for export based on date range
  const getExportTransactions = () => {
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    endDate.setHours(23, 59, 59, 999); // Include full end date
    
    return transactions.filter(t => {
      const txnDate = new Date(t.timestamp);
      return txnDate >= startDate && txnDate <= endDate;
    });
  };

  // Calculate tax summary
  const calculateTaxSummary = (txns) => {
    let totalDeposits = 0;
    let totalWithdrawals = 0;
    let totalBuys = 0;
    let totalSells = 0;
    let totalFees = 0;
    let realizedGains = 0;

    txns.forEach(t => {
      totalFees += t.fee || 0;
      switch (t.type) {
        case 'deposit':
          totalDeposits += t.amount;
          break;
        case 'withdraw':
          totalWithdrawals += t.amount;
          break;
        case 'buy':
          totalBuys += t.amount;
          break;
        case 'sell':
          totalSells += t.amount;
          // Simplified gain calculation (in real app, would track cost basis)
          realizedGains += t.amount * 0.1; // Assume 10% average gain for demonstration
          break;
        case 'business_invest':
          totalBuys += t.amount;
          break;
      }
    });

    return {
      totalDeposits,
      totalWithdrawals,
      totalBuys,
      totalSells,
      totalFees,
      realizedGains,
      netProfit: totalSells - totalBuys
    };
  };

  // Export to CSV
  const exportToCSV = () => {
    const exportTxns = getExportTransactions();
    const summary = calculateTaxSummary(exportTxns);
    
    // CSV header
    let csv = 'Date,Type,Description,Amount,Fee,Status,Currency\n';
    
    // Transaction rows
    exportTxns.forEach(t => {
      const date = new Date(t.timestamp).toISOString().split('T')[0];
      const amount = ['deposit', 'sell'].includes(t.type) ? t.amount : -t.amount;
      csv += `${date},${t.type},"${t.description || ''}",${amount.toFixed(2)},${(t.fee || 0).toFixed(2)},${t.status},USD\n`;
    });
    
    // Add summary section
    csv += '\n\nTAX SUMMARY\n';
    csv += `Period,${dateRange.startDate} to ${dateRange.endDate}\n`;
    csv += `Total Deposits,${summary.totalDeposits.toFixed(2)}\n`;
    csv += `Total Withdrawals,${summary.totalWithdrawals.toFixed(2)}\n`;
    csv += `Total Buys,${summary.totalBuys.toFixed(2)}\n`;
    csv += `Total Sells,${summary.totalSells.toFixed(2)}\n`;
    csv += `Total Fees Paid,${summary.totalFees.toFixed(2)}\n`;
    csv += `Net Trading Activity,${summary.netProfit.toFixed(2)}\n`;
    
    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sraivest_tax_report_${dateRange.startDate}_to_${dateRange.endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success('CSV exported successfully!');
  };

  // Export to PDF
  const exportToPDF = () => {
    const exportTxns = getExportTransactions();
    const summary = calculateTaxSummary(exportTxns);
    
    // Create PDF-like HTML content
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Sraivest Tax Report</title>
        <style>
          body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 40px; }
          h1 { color: #00E676; border-bottom: 2px solid #00E676; padding-bottom: 10px; }
          h2 { color: #333; margin-top: 30px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
          th { background-color: #0A0A0A; color: white; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .summary-box { background: #f5f5f5; padding: 20px; border-radius: 8px; margin: 20px 0; }
          .summary-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #ddd; }
          .summary-row:last-child { border-bottom: none; font-weight: bold; }
          .positive { color: #00E676; }
          .negative { color: #FF3B30; }
          .header-info { display: flex; justify-content: space-between; margin-bottom: 20px; }
          .logo { font-size: 24px; font-weight: bold; color: #00E676; }
        </style>
      </head>
      <body>
        <div class="header-info">
          <div class="logo">SRAIVEST FINANCIAL</div>
          <div>Generated: ${new Date().toLocaleDateString()}</div>
        </div>
        
        <h1>Tax Report</h1>
        <p><strong>Period:</strong> ${dateRange.startDate} to ${dateRange.endDate}</p>
        <p><strong>Total Transactions:</strong> ${exportTxns.length}</p>
        
        <h2>Summary</h2>
        <div class="summary-box">
          <div class="summary-row"><span>Total Deposits</span><span class="positive">$${summary.totalDeposits.toFixed(2)}</span></div>
          <div class="summary-row"><span>Total Withdrawals</span><span class="negative">$${summary.totalWithdrawals.toFixed(2)}</span></div>
          <div class="summary-row"><span>Total Asset Purchases</span><span class="negative">$${summary.totalBuys.toFixed(2)}</span></div>
          <div class="summary-row"><span>Total Asset Sales</span><span class="positive">$${summary.totalSells.toFixed(2)}</span></div>
          <div class="summary-row"><span>Total Fees Paid</span><span class="negative">$${summary.totalFees.toFixed(2)}</span></div>
          <div class="summary-row"><span>Net Trading Activity</span><span class="${summary.netProfit >= 0 ? 'positive' : 'negative'}">$${summary.netProfit.toFixed(2)}</span></div>
        </div>
        
        <h2>Transaction Details</h2>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Description</th>
              <th>Amount</th>
              <th>Fee</th>
            </tr>
          </thead>
          <tbody>
            ${exportTxns.map(t => `
              <tr>
                <td>${(t.timestamp || t.created_at ? new Date(t.timestamp || t.created_at).toLocaleDateString() : "Date unavailable")}</td>
                <td style="text-transform: capitalize;">${t.type.replace('_', ' ')}</td>
                <td>${t.description || '-'}</td>
                <td class="${['deposit', 'sell'].includes(t.type) ? 'positive' : 'negative'}">
                  ${['deposit', 'sell'].includes(t.type) ? '+' : '-'}$${Math.abs(t.amount).toFixed(2)}
                </td>
                <td>$${(t.fee || 0).toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <p style="margin-top: 40px; font-size: 12px; color: #666;">
          <strong>Disclaimer:</strong> This report is provided for informational purposes only and should not be considered tax advice. 
          Please consult with a qualified tax professional for your specific tax situation.
        </p>
      </body>
      </html>
    `;
    
    // Open in new window for printing
    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Trigger print dialog
    setTimeout(() => {
      printWindow.print();
    }, 500);
    
    toast.success('PDF report opened for printing!');
  };

  const handleExport = async (format) => {
    setExportLoading(true);
    try {
      if (format === 'csv') {
        exportToCSV();
      } else if (format === 'pdf') {
        exportToPDF();
      }
    } catch (error) {
      toast.error('Export failed. Please try again.');
    } finally {
      setExportLoading(false);
      setShowExportDialog(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="transactions-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Transaction History
            </h1>
            <p className="text-gray-400 mt-1">View all your account activity</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-dark pl-10 w-48"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="input-dark w-36">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-white/10">
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="deposit">Deposits</SelectItem>
                <SelectItem value="withdraw">Withdrawals</SelectItem>
                <SelectItem value="buy">Buys</SelectItem>
                <SelectItem value="sell">Sells</SelectItem>
                <SelectItem value="business_invest">Business</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              onClick={() => setShowExportDialog(true)} 
              className="btn-primary gap-2"
              data-testid="export-btn"
            >
              <Download className="w-4 h-4" />
              Export Tax Report
            </Button>
          </div>
        </div>

        <Card className="card-dashboard">
          <CardContent className="p-0">
            {filteredTransactions.length > 0 ? (
              <div className="divide-y divide-white/5">
                {filteredTransactions.map((txn, i) => (
                  <div key={i} className="flex items-center justify-between p-4 hover:bg-white/5 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getColor(txn.type)}`}>
                        {getIcon(txn.type)}
                      </div>
                      <div>
                        <p className="font-medium text-white capitalize">{txn.type.replace('_', ' ')}</p>
                        <p className="text-sm text-gray-500 max-w-xs truncate">{txn.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right hidden sm:block">
                        {txn.fee > 0 && (
                          <p className="text-xs text-gray-500">Fee: {formatCurrency(txn.fee)}</p>
                        )}
                        <p className="text-xs text-gray-500 capitalize">{txn.status}</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold number ${
                          ['deposit', 'sell'].includes(txn.type) ? 'text-[#00E676]' : 'text-[#FF3B30]'
                        }`}>
                          {['deposit', 'sell'].includes(txn.type) ? '+' : '-'}
                          {formatCurrency(Math.abs(txn.amount))}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(txn.timestamp).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <TrendingUp className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No transactions found</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Export Dialog */}
        <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
          <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Download className="w-5 h-5 text-[#00E676]" />
                Export Tax Report
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-[#6366F1]" />
                  <span className="text-sm text-gray-400">Select Date Range</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-500">Start Date</Label>
                    <Input
                      type="date"
                      value={dateRange.startDate}
                      onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
                      className="input-dark"
                      data-testid="export-start-date"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-500">End Date</Label>
                    <Input
                      type="date"
                      value={dateRange.endDate}
                      onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
                      className="input-dark"
                      data-testid="export-end-date"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm text-gray-400">Export Format</Label>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => handleExport('csv')}
                    disabled={exportLoading}
                    className="btn-secondary h-20 flex-col gap-2"
                    data-testid="export-csv-btn"
                  >
                    <FileSpreadsheet className="w-6 h-6" />
                    <span>CSV Spreadsheet</span>
                  </Button>
                  <Button
                    onClick={() => handleExport('pdf')}
                    disabled={exportLoading}
                    className="btn-secondary h-20 flex-col gap-2"
                    data-testid="export-pdf-btn"
                  >
                    <FileText className="w-6 h-6" />
                    <span>PDF Report</span>
                  </Button>
                </div>
              </div>

              <p className="text-xs text-gray-500 mt-4">
                Your tax report will include all transactions within the selected date range, 
                along with a summary of deposits, withdrawals, trades, and fees.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
